/**
 * Users Datasource (Infrastructure)
 *
 * Handles raw API communication for users.
 * Similar to Flutter's datasource pattern.
 *
 * This layer is responsible for:
 * - Making HTTP requests
 * - Parsing raw responses
 * - Throwing network exceptions
 */

import { post, get, put } from '@/shared/api';

import type { UserDetail } from '../../domain/types';
import type { UserSearchRequest } from '../../domain/types/requests';
import type { UserSearchResponse, UserSearchOptionsResponse } from '../../domain/types/responses';

// ============================================================================
// Datasource Interface
// ============================================================================

export interface UpdateUserRequest {
  authenticated_id: number;
  first_name: string;
  last_name: string;
  email: string;
  mobile_number: string;
  extension: number;
  is_active: boolean;
}

export interface UsersDatasource {
  searchUsers(request: UserSearchRequest): Promise<UserSearchResponse>;
  getSearchOptions(): Promise<UserSearchOptionsResponse>;
  updateUser(request: UpdateUserRequest): Promise<UserDetail>;
}

// ============================================================================
// Datasource Implementation
// ============================================================================

export class UsersDatasourceImpl implements UsersDatasource {
  async searchUsers(request: UserSearchRequest): Promise<UserSearchResponse> {
    // Map domain request to API format
    // Note: The /user/search/paginated endpoint uses 'column' for both filters and sorts, not 'name'
    const apiRequest = {
      filters: request.filters.map(f => ({
        column: f.name, // Map 'name' to 'column' for the API
        values: f.values,
      })),
      sorts: request.sorts.map(s => ({
        column: s.name, // Map 'name' to 'column' for the API
        order: s.order ?? 'asc',
      })),
      page: request.page,
      per_page: request.per_page,
      search: request.search ? { text: request.search.text } : undefined,
    };

    // Map search object to API format
    if (request.search?.text) {
      apiRequest.search = { text: request.search.text };
    }

    interface ApiResponse {
      items: unknown[];
      total: number;
      page: number;
      per_page: number;
      pages?: number;
    }

    const response = await post<ApiResponse>('/app/user/search/paginated', apiRequest);

    // Map API response to domain format
    // API returns: { items, total, page, per_page }
    // Domain expects: { users, total, page, pages }
    const perPage = response.per_page;
    const total = response.total;
    const pages = response.pages ?? Math.ceil(total / perPage);

    return {
      users: response.items as UserDetail[],
      total,
      page: response.page,
      pages,
    };
  }

  async getSearchOptions(): Promise<UserSearchOptionsResponse> {
    // Try GET /user/search for options (similar to case search pattern)
    // If it doesn't exist, return empty/default columns
    try {
      const response = await get<UserSearchOptionsResponse>('/app/user/search/paginated');
      return response;
    } catch (_error) {
      // If GET endpoint doesn't exist, return empty options
      // This allows the implementation to work even if GET endpoint is not available
      console.warn('[UsersDatasource] GET /user/search not available, returning empty options');
      return {
        columns: [],
        options: [],
      };
    }
  }

  async updateUser(request: UpdateUserRequest): Promise<UserDetail> {
    const response = await put<UserDetail>('/user', request);
    return response;
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

export const usersDatasource: UsersDatasource = new UsersDatasourceImpl();
